import React, { useEffect } from 'react'
import { useFetch } from 'hooks/useFetch'
import SectionError from 'components/error/SectionError'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import moment from 'moment'
import Loader from 'components/loader/Loader'

/* ── design tokens ── */
const T = {
  bg:   '#F6F5F0',
  card: '#FFFFFF',
  line: '#E5E2D8',
  ink:  '#232821',
  mut:  '#6E766C',
  acc:  '#2E9E7A',
  accL: 'rgba(46,158,122,0.15)',
};

/* ── mini bar chart via Highcharts ── */
const MiniBarChart = ({ data, categories }) => {
  // Format category dates → "FY21" style
  const cats = (categories || []).map(d => {
    const flat = Array.isArray(d) ? d[0] : d
    if (!flat) return ''
    const y = moment(flat).format('YY')
    return 'FY' + y
  })

  const vals = (data || []).map(d => {
    const flat = Array.isArray(d) ? d[0] : d
    return typeof flat === 'number' ? flat : parseFloat(flat) || 0
  })

  const options = {
    chart: {
      type: 'column',
      height: 110,
      backgroundColor: 'transparent',
      margin: [8, 0, 28, 0],
      spacing: [0, 0, 0, 0],
    },
    title: { text: null },
    credits: { enabled: false },
    exporting: { enabled: false },
    legend: { enabled: false },
    xAxis: {
      categories: cats,
      lineWidth: 0,
      tickLength: 0,
      labels: {
        style: {
          fontSize: '10px',
          color: T.mut,
          fontFamily: "'Instrument Sans', system-ui, sans-serif",
        },
      },
    },
    yAxis: {
      gridLineWidth: 0,
      lineWidth: 0,
      title: null,
      labels: { enabled: false },
      min: 0,
    },
    tooltip: {
      formatter: function () {
        return `<b>${this.x}</b><br/>${Highcharts.numberFormat(this.y, 1)}`
      },
      borderColor: T.acc,
      borderWidth: 1,
      shadow: false,
      backgroundColor: T.card,
      style: { color: T.ink, fontSize: '11px' },
    },
    plotOptions: {
      column: {
        borderRadius: 3,
        borderWidth: 0,
        color: T.acc,
        pointPadding: 0.08,
        groupPadding: 0.1,
        dataLabels: { enabled: false },
        states: {
          hover: { color: '#25876A' },
        },
      },
    },
    series: [{ data: vals, name: '' }],
  }

  return (
    <HighchartsReact highcharts={Highcharts} options={options} />
  )
}

/* ── single growth card ── */
const GrowthCard = ({ title, badge, year, data, yr1, yr3, yr5 }) => {
  const isNeg = (v) => v && v.startsWith('-')

  return (
    <div style={{
      background: T.card,
      borderRadius: 16,
      border: `1px solid ${T.line}`,
      padding: '14px 16px 16px',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 2 }}>
        <span style={{
          fontFamily: "'Bricolage Grotesque', sans-serif",
          fontSize: 13,
          fontWeight: 700,
          color: T.ink,
          lineHeight: 1.2,
        }}>
          {title}
        </span>
        <span style={{
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: '0.07em',
          color: T.mut,
          textTransform: 'uppercase',
          marginTop: 2,
        }}>
          {badge}
        </span>
      </div>

      {/* Bar chart */}
      <div style={{ margin: '0 -4px' }}>
        <MiniBarChart data={data} categories={year} />
      </div>

      {/* Stats row — 1 Yr · 3 Yrs · 5 Yrs */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        borderTop: `1px solid ${T.line}`,
        marginTop: 6,
        paddingTop: 10,
        gap: 4,
      }}>
        {[
          { label: '1 Yr',  val: yr1 },
          { label: '3 Yrs', val: yr3 },
          { label: '5 Yrs', val: yr5 },
        ].map(({ label, val }) => (
          <div key={label} style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 10, color: T.mut, marginBottom: 1 }}>{label}</div>
            <div style={{
              fontSize: 13,
              fontWeight: 700,
              color: isNeg(val) ? '#D9614F' : T.acc,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {val || '—'}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ── main container ── */
const RatioContainer = ({ url }) => {
  const { data, loading, error, retry } = useFetch(url)

  if (loading) {
    return (
      <div style={{
        background: T.card,
        borderRadius: 16,
        border: `1px solid ${T.line}`,
        padding: 20,
        marginBottom: 16,
      }}>
        <Loader />
      </div>
    )
  }

  if (error) {
    return (
      <div style={{
        background: T.card,
        borderRadius: 16,
        border: `1px solid ${T.line}`,
        padding: 20,
        marginBottom: 16,
      }}>
        <SectionError message={error.message} onRetry={retry} />
      </div>
    )
  }

  const charts = [
    {
      title: 'Sales Growth',
      badge: 'CAGR',
      year:  data.five_year_profitandloss?.map(i => [i.as_on_date]) || [],
      data:  data.five_year_profitandloss?.map(i => [i.sales]) || [],
      yr1:   (data.sales_cagr?.sales_cagr_one_year   || null) && data.sales_cagr.sales_cagr_one_year   + '%',
      yr3:   (data.sales_cagr?.sales_cagr_three_year || null) && data.sales_cagr.sales_cagr_three_year + '%',
      yr5:   (data.sales_cagr?.sales_cagr_five_year  || null) && data.sales_cagr.sales_cagr_five_year  + '%',
    },
    {
      title: 'Profit Growth',
      badge: 'CAGR',
      year:  data.five_year_profitandloss?.map(i => [i.as_on_date]) || [],
      data:  data.five_year_profitandloss?.map(i => [i.net_profit]) || [],
      yr1:   (data.profit_cagr?.profit_cagr_one_year   || null) && data.profit_cagr.profit_cagr_one_year   + '%',
      yr3:   (data.profit_cagr?.profit_cagr_three_year || null) && data.profit_cagr.profit_cagr_three_year + '%',
      yr5:   (data.profit_cagr?.profit_cagr_five_year  || null) && data.profit_cagr.profit_cagr_five_year  + '%',
    },
    {
      title: 'Stock Price',
      badge: 'CAGR',
      year:  data.five_year_stock_price?.map(i => [i.as_on_date]) || [],
      data:  data.five_year_stock_price?.map(i => [i.stock_price]) || [],
      yr1:   (data.stock_price_cagr?.stock_price_cagr_one_year   || null) && data.stock_price_cagr.stock_price_cagr_one_year   + '%',
      yr3:   (data.stock_price_cagr?.stock_price_cagr_three_year || null) && data.stock_price_cagr.stock_price_cagr_three_year + '%',
      yr5:   (data.stock_price_cagr?.stock_price_cagr_five_year  || null) && data.stock_price_cagr.stock_price_cagr_five_year  + '%',
    },
    {
      title: 'Return on Equity',
      badge: 'AVERAGE',
      year:  data.five_year_roe?.map(i => [i.as_on_date]) || [],
      data:  data.five_year_roe?.map(i => [i.roe]) || [],
      yr1:   (data.roe_avg?.avg_roe_one_year   || null) && data.roe_avg.avg_roe_one_year   + '%',
      yr3:   (data.roe_avg?.avg_roe_three_year || null) && data.roe_avg.avg_roe_three_year + '%',
      yr5:   (data.roe_avg?.avg_roe_five_year  || null) && data.roe_avg.avg_roe_five_year  + '%',
    },
  ]

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: 12,
      marginBottom: 16,
    }}>
      {charts.map((c, i) => (
        <GrowthCard key={i} {...c} />
      ))}
    </div>
  )
}

export default RatioContainer